import { CLINIC_INFO } from '../data';
import { Phone, Mail, MapPin, Clock, MessageSquare, ExternalLink, Facebook } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import { motion } from 'framer-motion';
import { useEffect } from 'react';

export default function Contact() {
  useEffect(() => {
    document.title = "CDH Sports- og Wellness Massage - Kontakt";
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-[75vh] lg:h-[80vh] flex items-center overflow-hidden pt-32 md:pt-40 lg:pt-48">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/111cf125-e389-46d1-8c9b-aaa41d94cdba.jpg"
            alt="Kontakt CDH Massage"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/60 z-10" />
        </div>

        <div className="container mx-auto px-4 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-[1.1]">
              Kontakt mig
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-white/90 mb-10 leading-relaxed font-light">
              Har du spørgsmål eller brug for hjælp? Jeg sidder klar til at hjælpe dig med at finde den rette behandling eller svare på dine spørgsmål.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <a 
                href="#formular" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3.5 lg:px-8 lg:py-4 rounded-full text-base lg:text-lg font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Send en besked
              </a>
              <a 
                href={`tel:${CLINIC_INFO.phone}`} 
                className="bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/30 px-6 py-3.5 lg:px-8 lg:py-4 rounded-full text-base lg:text-lg font-bold transition-all flex items-center justify-center"
              >
                Ring til mig
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="formular" className="py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col gap-16">
            
            {/* Row 1: Contact Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-blue-50 p-8 rounded-[2rem] border border-blue-100 flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-bold text-blue-900 mb-8 flex items-center">
                    <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    </span>
                    Kontaktinformation
                  </h3>
                  <ul className="space-y-6">
                    <li className="flex items-start">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mr-4 shadow-sm border border-blue-100 shrink-0">
                        <Phone className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Telefon</p>
                        <a href={`tel:${CLINIC_INFO.phone}`} className="text-base md:text-lg font-bold text-blue-900 hover:text-blue-700 transition-colors">
                          {CLINIC_INFO.phone}
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mr-4 shadow-sm border border-blue-100 shrink-0">
                        <Mail className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">E-mail</p>
                        <a href={`mailto:${CLINIC_INFO.email}`} className="text-base md:text-lg font-bold text-blue-900 hover:text-blue-700 transition-colors break-all">
                          {CLINIC_INFO.email}
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mr-4 shadow-sm border border-blue-100 shrink-0">
                        <MapPin className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Adresse</p>
                        <a 
                          href="https://maps.app.goo.gl/zduoinBMcwsKXgZPA"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-base md:text-lg font-bold text-blue-900 leading-tight hover:text-blue-700 transition-colors"
                        >
                          {CLINIC_INFO.address}
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mr-4 shadow-sm border border-blue-100 shrink-0">
                        <Facebook className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Facebook</p>
                        <a 
                          href="https://www.facebook.com/CDHSport/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-base md:text-lg font-bold text-blue-900 hover:text-blue-700 transition-colors"
                        >
                          CDH Sport & Wellness
                        </a>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-emerald-50 p-8 rounded-[2rem] border border-emerald-100 flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-bold text-blue-900 mb-6 flex items-center">
                    <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                      <Clock className="w-5 h-5 text-emerald-600" />
                    </span>
                    Telefontider
                  </h3>
                  <div className="space-y-4">
                    <p className="text-base font-bold text-blue-900">
                      Inden for normal åbningstid (10.00-16.30)
                    </p>
                    <p className="text-xs sm:text-sm text-emerald-800 leading-relaxed">
                      Du er altid velkommen til at ringe til mig i løbet af mine normale åbningstider. Hvis jeg er optaget af behandlinger og ikke svarer, kan du blot lægge en besked eller sende en SMS.
                    </p>
                  </div>
                </div>
                <div className="mt-8 p-4 bg-white/50 rounded-2xl border border-emerald-100">
                  <p className="text-[11px] sm:text-xs text-emerald-800 leading-relaxed font-medium">
                    Send gerne en SMS hvis jeg ikke tager telefonen – jeg ringer tilbage så snart jeg er ledig.
                  </p>
                </div>
              </div>
            </div>

            {/* Row 2: Contact Form */}
            <div className="w-full">
              <div className="bg-white p-8 md:p-12 rounded-[3rem] shadow-xl border border-gray-100">
                <div className="mb-10">
                  <h2 className="text-3xl font-bold text-blue-900 mb-4">Send mig en besked</h2>
                  <p className="text-gray-600 text-sm sm:text-base">
                    Udfyld formularen herunder, så vender jeg tilbage til dig inden for 24 timer på hverdage.
                  </p>
                </div>
                <ContactForm />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[300px] md:h-[400px] lg:h-[500px] w-full bg-gray-100 relative grayscale hover:grayscale-0 transition-all duration-700">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2275.9405615701833!2d9.324311877239304!3d55.06648797300728!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b33709b1f3c3a1%3A0xb3c3e3e3e3e3e3e3!2sBaneg%C3%A5rdspladsen%202%2C%206230%20R%C3%B8dekro!5e0!3m2!1sda!2sdk!4v1700000000000!5m2!1sda!2sdk"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="CDH Massage Lokation"
        ></iframe>
      </section>
    </div>
  );
}
